import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useQuery, type QueryKey } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { usePageFilterPanel } from "@/app/pageActions/PageActionsContext";
import {
  type ListQueryParams,
  type PagedResult,
} from "@/shared/list/listQueryTypes";
import {
  parseListSearchParams,
  writeListSearchParams,
} from "@/shared/list/listSearchParams";
import { DEFAULT_LIST_PAGE_SIZE, type ListPageSize } from "@/shared/list/pagination";
import { useMediaQuery } from "@/shared/lib/useMediaQuery";

const Q_DEBOUNCE_MS = 300;
const DESKTOP_MQ = "(min-width: 1024px)";

export type UseServerListQueryArgs<T> = {
  /** Stable query key prefix, e.g. `["sys","users"]`. */
  queryKey: QueryKey;
  fetcher: (params: ListQueryParams) => Promise<PagedResult<T>>;
  /** Extra filter keys synced to URL + API (e.g. `roleCode`). */
  filterKeys?: readonly string[];
  enabled?: boolean;
  /** TestUI lists keep the compact search row visible on first paint. */
  filtersInitialOpen?: boolean;
  /** Hide the toolbar filter toggle when this list is not the visible surface. */
  filtersPanelEnabled?: boolean;
  /** Rows per page when the URL has no `pageSize`. */
  defaultPageSize?: ListPageSize;
  /** Force mobile behavior (accumulated list + loadMore) regardless of screen width. */
  forceMobile?: boolean;
};

/**
 * Server-driven list: URL ↔ query params ↔ TanStack Query.
 * Mobile (&lt; lg): accumulates pages for card list + load-more.
 * Do not use for client-side slice of master lists.
 */
export function useServerListQuery<T>({
  queryKey,
  fetcher,
  filterKeys = [],
  enabled = true,
  filtersInitialOpen = true,
  filtersPanelEnabled = true,
  defaultPageSize = DEFAULT_LIST_PAGE_SIZE,
  forceMobile = false,
}: UseServerListQueryArgs<T>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const mediaIsDesktop = useMediaQuery(DESKTOP_MQ);
  const isDesktop = forceMobile ? false : mediaIsDesktop;

  const urlParams = useMemo(
    () => parseListSearchParams(searchParams, filterKeys, defaultPageSize),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filterKeys expected stable
    [searchParams, filterKeys.join("|"), defaultPageSize],
  );

  const [qInput, setQInput] = useState(urlParams.q);
  const [debouncedQ, setDebouncedQ] = useState(urlParams.q);

  useEffect(() => {
    setQInput(urlParams.q);
    setDebouncedQ(urlParams.q);
  }, [urlParams.q]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(qInput), Q_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [qInput]);

  const effectiveParams: ListQueryParams = useMemo(
    () => ({
      ...urlParams,
      q: debouncedQ,
    }),
    [urlParams, debouncedQ],
  );

  useEffect(() => {
    if (debouncedQ === urlParams.q) return;
    const next: ListQueryParams = {
      ...urlParams,
      q: debouncedQ,
      page: 1,
    };
    setSearchParams(writeListSearchParams(next, filterKeys, defaultPageSize), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  const query = useQuery({
    queryKey: [...queryKey, effectiveParams],
    queryFn: () => fetcher(effectiveParams),
    enabled,
    /** Keep prior page while paging; do not keep rows from another status/q (KPI vs cột lệch). */
    placeholderData: (previousData, previousQuery) => {
      const prevKey = previousQuery?.queryKey;
      if (!previousData || !Array.isArray(prevKey) || prevKey.length === 0) return undefined;
      const prevParams = prevKey[prevKey.length - 1] as ListQueryParams | undefined;
      if (!prevParams || typeof prevParams !== "object") return undefined;
      if (
        prevParams.q !== effectiveParams.q ||
        prevParams.status !== effectiveParams.status ||
        JSON.stringify(prevParams.filters ?? {}) !== JSON.stringify(effectiveParams.filters ?? {}) ||
        prevParams.pageSize !== effectiveParams.pageSize
      ) {
        return undefined;
      }
      return previousData;
    },
  });

  const patchParams = (patch: Partial<ListQueryParams> & { filters?: Record<string, string> }) => {
    const next: ListQueryParams = {
      ...urlParams,
      ...patch,
      q: patch.q !== undefined ? patch.q : urlParams.q,
      filters: patch.filters
        ? { ...urlParams.filters, ...patch.filters }
        : urlParams.filters,
    };
    if (patch.q !== undefined) {
      setQInput(patch.q);
      setDebouncedQ(patch.q);
    }
    setSearchParams(writeListSearchParams(next, filterKeys, defaultPageSize), { replace: true });
  };

  const setPage = (page: number) => patchParams({ page });
  const setPageSize = (pageSize: ListPageSize | number) =>
    patchParams({ pageSize: pageSize as ListPageSize, page: 1 });
  const setStatus = (status: string) => patchParams({ status, page: 1 });
  const setFilter = (key: string, value: string) =>
    patchParams({ filters: { [key]: value }, page: 1 });

  const clearFilters = () => {
    setQInput("");
    setDebouncedQ("");
    setSearchParams(writeListSearchParams(defaultCleared(urlParams.pageSize), filterKeys, defaultPageSize), {
      replace: true,
    });
  };

  const hasStatusFilter = urlParams.status !== "all";
  const activeFilterCount =
    (qInput.trim() ? 1 : 0) +
    (hasStatusFilter ? 1 : 0) +
    filterKeys.reduce((n, k) => n + (urlParams.filters[k] && urlParams.filters[k] !== "all" ? 1 : 0), 0);

  const filtersOpen = usePageFilterPanel({
    initialOpen: filtersInitialOpen,
    activeCount: activeFilterCount,
    // Keep filter chrome even when query is gated (e.g. stock requires WH first).
    enabled: filtersPanelEnabled,
  });

  const data = query.data;
  const total = data?.total ?? 0;
  const page = data?.page ?? effectiveParams.page;
  const pageSize = data?.pageSize ?? effectiveParams.pageSize;
  const pageItems = data?.items ?? [];

  /** Filter signature — reset accumulated pages when search/filters change. */
  const filterSig = useMemo(
    () =>
      JSON.stringify({
        q: effectiveParams.q,
        status: effectiveParams.status,
        filters: effectiveParams.filters,
        pageSize: effectiveParams.pageSize,
      }),
    [effectiveParams.q, effectiveParams.status, effectiveParams.filters, effectiveParams.pageSize],
  );

  const pagesRef = useRef<Map<number, T[]>>(new Map());
  const [accumulated, setAccumulated] = useState<T[]>([]);
  const filterSigRef = useRef(filterSig);

  useEffect(() => {
    if (isDesktop) {
      pagesRef.current = new Map();
      setAccumulated([]);
      return;
    }
    if (filterSigRef.current !== filterSig) {
      filterSigRef.current = filterSig;
      pagesRef.current = new Map();
      setAccumulated([]);
      return;
    }
    if (!data) return;
    pagesRef.current.set(data.page, data.items);
    const merged = [...pagesRef.current.entries()]
      .sort((a, b) => a[0] - b[0])
      .flatMap(([, rows]) => rows);
    setAccumulated(merged);
  }, [data, filterSig, isDesktop]);

  useEffect(() => {
    // If mobile list and loaded page has no items while total > 0 (or page > 1 with 0 items),
    // reset to page 1 to prevent getting stuck or looping infinitely.
    if (!isDesktop && data && data.items.length === 0 && data.total > 0 && data.page > 1) {
      setPage(1);
    }
  }, [isDesktop, data]);

  const items = isDesktop ? pageItems : accumulated.length > 0 ? accumulated : pageItems;
  const rowNumberStart = isDesktop
    ? total === 0
      ? 1
      : (page - 1) * pageSize + 1
    : 1;
  const hasMore =
    !isDesktop &&
    total > 0 &&
    items.length < total &&
    Boolean(data && data.items.length > 0) &&
    (data?.totalPages ? page < data.totalPages : true);

  const loadMore = useCallback(() => {
    if (isDesktop || query.isFetching) return;
    if (!data || data.items.length === 0) return;
    if (data.totalPages && page >= data.totalPages) return;
    const loaded = accumulated.length > 0 ? accumulated.length : pageItems.length;
    if (loaded >= total) return;
    setPage(page + 1);
  }, [
    isDesktop,
    query.isFetching,
    accumulated.length,
    pageItems.length,
    total,
    page,
    data,
  ]);

  return {
    ...query,
    items,
    total,
    page,
    pageSize,
    rowNumberStart,
    q: qInput,
    setQ: setQInput,
    debouncedQ,
    status: urlParams.status,
    setStatus,
    filters: urlParams.filters,
    setFilter,
    setParams: patchParams,
    filtersOpen,
    activeFilterCount,
    clearFilters,
    setPage,
    setPageSize,
    params: effectiveParams,
    isDesktop,
    hasMore,
    loadMore,
    shown: items.length,
  };
}

function defaultCleared(pageSize: number): ListQueryParams {
  return {
    page: 1,
    pageSize,
    q: "",
    status: "all",
    filters: {},
  };
}
